import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileText, Download, Calendar, IndianRupee } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

const FineReport = () => {
  const [lateFines, setLateFines] = useState([]);
  const [damageFines, setDamageFines] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set default date range (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    setFromDate(firstDay);
    setToDate(lastDay);
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      fetchFines();
    }
  }, [fromDate, toDate]);

  const fetchFines = async () => {
    setLoading(true);
    try {
      // Fetch late return fines (from book issues)
      const issuesResponse = await axios.get(`${API_URL}/api/library/issues`, { headers: getAuthHeaders() });
      const issues = Array.isArray(issuesResponse.data) ? issuesResponse.data : [];
      
      const lateReturns = issues.filter(issue => {
        if (issue.status !== 'Returned' || !issue.fine_amount || issue.fine_amount <= 0) return false;
        const returnDate = issue.return_date;
        if (!returnDate) return false;
        return returnDate >= fromDate && returnDate <= toDate;
      });
      setLateFines(lateReturns);

      // Fetch damage fines
      const damageResponse = await axios.get(`${API_URL}/api/library/damaged-books`, { headers: getAuthHeaders() });
      const damages = Array.isArray(damageResponse.data) ? damageResponse.data : [];
      
      const paidDamages = damages.filter(d => {
        if (!d.penalty_paid) return false;
        const damageDate = d.damage_date;
        if (!damageDate) return false;
        return damageDate >= fromDate && damageDate <= toDate;
      });
      setDamageFines(paidDamages);
    } catch (error) {
      console.error('Error fetching fines:', error);
    } finally {
      setLoading(false);
    }
  };

  // Bug fix: removed shadowing formatDate – using imported one

  const totalLateFines = lateFines.reduce((sum, f) => sum + (f.fine_amount || 0), 0);
  const totalDamageFines = damageFines.reduce((sum, f) => sum + (f.penalty_amount || 0), 0);
  const grandTotal = totalLateFines + totalDamageFines;

  const exportToCSV = () => {
    let csv = 'Type,Book,Member,Date,Amount\n';
    
    lateFines.forEach(f => {
      csv += `Late Return Fine,"${f.book_title}","${f.member_name}",${f.return_date},${f.fine_amount}\n`;
    });
    
    damageFines.forEach(f => {
      csv += `Damage Fine,"${f.book_title}","${f.member_name || 'N/A'}",${f.damage_date},${f.penalty_amount}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `library_fine_report_${fromDate}_to_${toDate}.csv`;
    a.click();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6" /> Library Fine Report
        </h1>
        <Button onClick={exportToCSV} variant="outline" className="gap-2">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      {/* Date Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-sm font-medium mb-1">From Date</label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">To Date</label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-40"
              />
            </div>
            <Button onClick={fetchFines}>Apply Filter</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Late Return Fines</p>
                <p className="text-2xl font-bold text-blue-800">₹{totalLateFines.toFixed(2)}</p>
                <p className="text-sm text-blue-600">{lateFines.length} entries</p>
              </div>
              <Calendar className="w-10 h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-600 text-sm font-medium">Damage Fines</p>
                <p className="text-2xl font-bold text-orange-800">₹{totalDamageFines.toFixed(2)}</p>
                <p className="text-sm text-orange-600">{damageFines.length} entries</p>
              </div>
              <FileText className="w-10 h-10 text-orange-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Total Collection</p>
                <p className="text-2xl font-bold text-green-800">₹{grandTotal.toFixed(2)}</p>
                <p className="text-sm text-green-600">{lateFines.length + damageFines.length} total entries</p>
              </div>
              <IndianRupee className="w-10 h-10 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Late Return Fines Table */}
      <Card>
        <CardHeader>
          <CardTitle>Late Return Fines</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-4">Loading...</p>
          ) : lateFines.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No late return fines in selected period</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3">Book</th>
                    <th className="text-left p-3">Member</th>
                    <th className="text-left p-3">Issue Date</th>
                    <th className="text-left p-3">Due Date</th>
                    <th className="text-left p-3">Return Date</th>
                    <th className="text-left p-3">Fine Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lateFines.map((fine) => (
                    <tr key={fine.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{fine.book_title}</td>
                      <td className="p-3">{fine.member_name}</td>
                      <td className="p-3">{formatDate(fine.issue_date)}</td>
                      <td className="p-3">{formatDate(fine.due_date)}</td>
                      <td className="p-3">{formatDate(fine.return_date)}</td>
                      <td className="p-3 font-bold text-blue-600">₹{fine.fine_amount}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-blue-50 font-bold">
                    <td colSpan="5" className="p-3 text-right">Total Late Return Fines:</td>
                    <td className="p-3 text-blue-600">₹{totalLateFines.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Damage Fines Table */}
      <Card>
        <CardHeader>
          <CardTitle>Damage Fines</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-4">Loading...</p>
          ) : damageFines.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No damage fines in selected period</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3">Book</th>
                    <th className="text-left p-3">Member</th>
                    <th className="text-left p-3">Damage Type</th>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Fine Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {damageFines.map((fine) => (
                    <tr key={fine.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{fine.book_title}</td>
                      <td className="p-3">{fine.member_name || 'N/A'}</td>
                      <td className="p-3">{fine.damage_type}</td>
                      <td className="p-3">{formatDate(fine.damage_date)}</td>
                      <td className="p-3 font-bold text-orange-600">₹{fine.penalty_amount}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-orange-50 font-bold">
                    <td colSpan="4" className="p-3 text-right">Total Damage Fines:</td>
                    <td className="p-3 text-orange-600">₹{totalDamageFines.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FineReport;
